import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import cn from 'classnames';
import DOMPurification from 'dompurify';
import capitalize from 'lodash.capitalize';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormField, FormItem, FormControl, FormMessage, } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { languageByCountryCode } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useHandleLinksManually } from '@/hooks/useHandleLinksManually';
import { useSocket } from '@/hooks/useSocket';
import { visitorInformationSelector } from '@/state/config';
import { emailState, sessionIdSelector, userSelector } from '@/state/session';
import { emailRegExp, validatePhoneNumber } from '@/util/utils';
const getValidationRules = (name, required, t, setValue) => ({
    name: {
        required: required ? t('fields.required', { field: name }) : undefined,
        maxLength: {
            value: 32,
            message: t('fields.less_than', { max: 32 }),
        },
    },
    email: {
        required: required ? t('fields.required', { field: name }) : undefined,
        pattern: {
            value: emailRegExp,
            message: t('fields.invalid', { field: name }),
        },
    },
    phone: {
        required: required ? t('fields.required', { field: name }) : undefined,
        validate: (value) => {
            if (!value)
                return true;
            const result = validatePhoneNumber(value);
            if (!result.valid)
                return t('fields.invalid', { field: name });
            // Format the phone number and update the form value
            // We use shouldValidate: false to prevent validation loop
            if (setValue) {
                setValue('phone', result.formatted, { shouldValidate: false });
            }
            return true;
        },
        maxLength: {
            value: 24,
            message: t('fields.less_than', { max: 24, field: name }),
        },
    },
    address: {
        required: required ? t('fields.required', { field: name }) : undefined,
        minLength: {
            value: 5,
            message: t('fields.more_than', { min: 5, field: name }),
        },
        maxLength: {
            value: 128,
            message: t('fields.less_than', { max: 128, field: name }),
        },
    },
    language: {
        required: required ? t('fields.required', { field: name }) : undefined,
    },
    disclaimer_accepted: {
        required: t('disclaimer.required'),
    },
});
export const WelcomeFields = () => {
    const sanitizer = DOMPurification.sanitize;
    const [isLoading, setIsLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const { updateContext } = useSocket();
    const sessionId = useRecoilValue(sessionIdSelector);
    const user = useRecoilValue(userSelector);
    const { headline, disclaimer, fields } = useRecoilValue(visitorInformationSelector);
    const languageField = fields === null || fields === void 0 ? void 0 : fields.find(({ field }) => field === 'language');
    const { trackEvent } = useAnalytics();
    const setEmail = useSetRecoilState(emailState);
    const { handleLinkClick } = useHandleLinksManually();
    const form = useForm({
        defaultValues: Object.assign(Object.assign({}, fields === null || fields === void 0 ? void 0 : fields.reduce((acc, { field }) => {
            var _a;
            return (Object.assign(Object.assign({}, acc), { [field === 'display_name' ? 'name' : field]: (user === null || user === void 0 ? void 0 : user[field]) &&
                    user[field] !== '***' &&
                    !((_a = user[field]) === null || _a === void 0 ? void 0 : _a.startsWith('Visitor '))
                    ? user[field]
                    : '' }));
        }, {})), { disclaimer_accepted: false }),
    });
    const isDisclaimerEnabled = !!(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.enabled) && !!(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.html);
    const hasFields = fields && fields.length > 0;
    // Reset the loading if the sessionId changes
    useEffect(() => {
        setIsLoading(false);
    }, [sessionId]);
    const onSubmit = useCallback(() => {
        const user = form.getValues();
        trackEvent(AnalyticsEvent.WelcomeFormSubmitted);
        const language = user === null || user === void 0 ? void 0 : user.language;
        if (language && language in languageByCountryCode) {
            i18n.changeLanguage(language);
        }
        if (user === null || user === void 0 ? void 0 : user.email) {
            setEmail(user.email);
        }
        // Update the context with `visitor_form_completed:true` so that we don't
        // Show this form again
        updateContext({
            user,
            global: Object.assign({ visitor_form_completed: true }, (isDisclaimerEnabled ? { disclaimer_accepted: true } : {})),
        });
        setIsLoading(true);
    }, [form, i18n, isDisclaimerEnabled, setEmail, trackEvent, updateContext]);
    const handleTermsLinkClick = useCallback((e) => {
        trackEvent(AnalyticsEvent.TermsAndConditionsLinkClicked);
        handleLinkClick(e);
    }, [handleLinkClick, trackEvent]);
    return (_jsx("div", { className: "relative flex h-full flex-1 flex-col justify-between overflow-hidden", children: _jsxs("div", { className: cn('mt-[var(--moveo-new-header-height)] flex h-full max-h-[calc(100%-68px)] flex-col overflow-auto px-6', {
                'justify-between': isDisclaimerEnabled && !hasFields,
            }), children: [headline && (_jsx("p", { className: "text-foreground mt-3 mb-2 text-lg leading-7 font-semibold", children: headline })), _jsx(Form, Object.assign({}, form, { children: _jsxs("form", { autoComplete: "off", className: "flex h-full flex-col", onSubmit: form.handleSubmit(onSubmit), children: [_jsx("div", { className: "mb-4", children: fields === null || fields === void 0 ? void 0 : fields.map(({ field, required }) => {
                                    const name = field === 'display_name' ? 'name' : field;
                                    if (field === 'language') {
                                        return (_jsx(FormField, { control: form.control, name: name, rules: getValidationRules(capitalize(name), required, t)[name], render: ({ field: controllerField }) => {
                                                var _a;
                                                return (_jsxs(FormItem, { children: [_jsx(FormControl, { children: _jsxs(Select, { value: controllerField.value, onValueChange: controllerField.onChange, children: [_jsx(SelectTrigger, { id: "language", className: "mt-4 w-full", children: _jsx(SelectValue, { placeholder: t('fields.language_placeholder') }) }), _jsx(SelectContent, { children: (_a = languageField === null || languageField === void 0 ? void 0 : languageField.languages) === null || _a === void 0 ? void 0 : _a.map((option) => (_jsx(SelectItem, { value: option, children: languageByCountryCode[option] }, option))) })] }) }), _jsx(FormMessage, {})] }));
                                            } }, name));
                                    }
                                    return (_jsx(FormField, { control: form.control, name: name, rules: getValidationRules(capitalize(name), required, t, form.setValue)[name], render: ({ field: controllerField }) => {
                                            return (_jsxs(FormItem, { children: [_jsx(FormControl, { children: _jsx(Input, Object.assign({}, controllerField, { className: "mt-4 w-full", placeholder: t(`fields.${field}`), id: name })) }), _jsx(FormMessage, {})] }));
                                        } }, name));
                                }) }), isDisclaimerEnabled && (_jsx("div", { className: "on mt-auto mb-20", children: _jsx(FormField, { control: form.control, name: "disclaimer_accepted", rules: getValidationRules('disclaimer_accepted', true, t)['disclaimer_accepted'], render: ({ field: controllerField }) => {
                                        return (_jsx(FormControl, { children: _jsxs(_Fragment, { children: [_jsxs("div", { className: "bg-bg-muted mt-auto rounded-[var(--moveo-new-border-radius-base)] p-4", children: [_jsx("p", { className: "text-foreground mb-2 text-sm leading-5 font-semibold", children: t('disclaimer.title') }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Checkbox, { id: "disclaimer_accepted", className: "bg-background translate-y-1.5", checked: controllerField.value, onCheckedChange: controllerField.onChange }), _jsx("div", { onClick: handleTermsLinkClick, role: "presentation", className: `${"disclaimer-container" /* TestClassName.DisclaimerContainer */} [&_a]:text-accent text-foreground [&_a]:cursor-pointer [&_a:hover]:underline`, dangerouslySetInnerHTML: {
                                                                            __html: sanitizer(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.html, {
                                                                                ADD_ATTR: ['target'],
                                                                            }),
                                                                        } })] })] }), _jsx(FormMessage, { className: "mt-2" })] }) }));
                                    } }) }))] }) })), _jsx("div", { className: "bg-background absolute right-0 bottom-0 left-0 z-10 mx-3 h-14", children: _jsx(Button, { isLoading: isLoading, className: "w-full", onClick: form.handleSubmit(onSubmit), children: t('buttons.continue') }) })] }) }));
};
